import type { AttachmentKind, OwnerType } from "@bt/domain";
import { ApiError } from "./errors";
import { apiPath } from "./garage";

const MAX_EDGE = 2048;
const THUMB_EDGE = 480;

export async function toWebp(bitmap: ImageBitmap, maxEdge: number, quality: number): Promise<Blob> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Encoding failed"))),
      "image/webp",
      quality,
    ),
  );
}

/**
 * Images are resized in the browser (max 2048 px, WebP) with a small thumbnail, so the server
 * needs no native image library. Other files are uploaded as they are.
 */
export async function uploadAttachment(input: {
  ownerType: OwnerType;
  ownerId: string;
  kind: AttachmentKind;
  file: File;
  title?: string;
}): Promise<string> {
  const form = new FormData();
  form.set("ownerType", input.ownerType);
  form.set("ownerId", input.ownerId);
  form.set("kind", input.kind);
  if (input.title) form.set("title", input.title);

  const isRaster = input.file.type.startsWith("image/") && input.file.type !== "image/svg+xml";
  if (input.kind === "image" && isRaster) {
    const bitmap = await createImageBitmap(input.file);
    const [full, thumb] = await Promise.all([
      toWebp(bitmap, MAX_EDGE, 0.86),
      toWebp(bitmap, THUMB_EDGE, 0.8),
    ]);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    form.set("width", String(Math.round(bitmap.width * scale)));
    form.set("height", String(Math.round(bitmap.height * scale)));
    bitmap.close();
    const baseName = input.file.name.replace(/\.[^.]+$/, "") || "image";
    form.set("file", new File([full], `${baseName}.webp`, { type: "image/webp" }));
    form.set("thumb", new File([thumb], `${baseName}-thumb.webp`, { type: "image/webp" }));
  } else {
    form.set("file", input.file);
  }

  const response = await fetch(apiPath("/files"), { method: "POST", body: form });
  if (!response.ok) throw await ApiError.from(response);
  const body = (await response.json().catch(() => ({}))) as { id?: string };
  if (!body.id) throw new ApiError(null);
  return body.id;
}

export function fileUrl(
  fileId: string,
  options: { thumb?: boolean; download?: boolean } = {},
): string {
  const params = new URLSearchParams();
  if (options.thumb) params.set("thumb", "1");
  if (options.download) params.set("download", "1");
  const query = params.toString();
  return apiPath(`/files/${fileId}${query ? `?${query}` : ""}`);
}

/** A profile picture: cropped to a square and made 256 px WebP in the browser. */
export async function uploadAvatar(file: File): Promise<void> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const square = await createImageBitmap(
    bitmap,
    Math.round((bitmap.width - side) / 2),
    Math.round((bitmap.height - side) / 2),
    side,
    side,
  );
  bitmap.close();
  const webp = await toWebp(square, 256, 0.86);
  square.close();
  const form = new FormData();
  form.set("file", new File([webp], "avatar.webp", { type: "image/webp" }));
  const response = await fetch("/api/account/avatar", { method: "POST", body: form });
  if (!response.ok) throw await ApiError.from(response);
}
